import { SYMBOLS } from '../apps/inversify/symbols'
import { inject, injectable } from '~/container'

const puFetchInjectionKey = SYMBOLS.NuxtAxiosInstance

export type PuFetchMethod =
  | 'get'
  | 'GET'
  | 'delete'
  | 'DELETE'
  | 'post'
  | 'POST'
  | 'put'
  | 'PUT'
  | 'patch'
  | 'PATCH'

export type PuFetchResponseType =
  | 'arraybuffer'
  | 'blob'
  | 'document'
  | 'json'
  | 'text'
  | 'stream'

export interface PuFetchRequestConfig {
  url?: string
  method?: PuFetchMethod
  baseURL?: string
  headers?: any
  params?: any
  paramsSerializer?: (params: any) => string
  data?: any
  mode?: 'cors'
  redirect?: 'manual'
  timeout?: number
  timeoutErrorMessage?: string
  withCredentials?: boolean
  credentials?: string
  responseType?: PuFetchResponseType
  xsrfCookieName?: string
  xsrfHeaderName?: string
  onUploadProgress?: (progressEvent: any) => void
  onDownloadProgress?: (progressEvent: any) => void
  maxContentLength?: number
  validateStatus?: ((status: number) => boolean) | null
  maxBodyLength?: number
  maxRedirects?: number
  socketPath?: string | null
  httpAgent?: any
  httpsAgent?: any
  decompress?: boolean
}

interface PuFetchRequest {
  $get<T = any>(url: string, config?: PuFetchRequestConfig): Promise<T>
  $delete<T = any>(url: string, config?: PuFetchRequestConfig): Promise<T>
  $post<T = any>(
    url: string,
    data?: any,
    config?: PuFetchRequestConfig,
  ): Promise<T>
  $put<T = any>(
    url: string,
    data?: any,
    config?: PuFetchRequestConfig,
  ): Promise<T>
  $patch<T = any>(
    url: string,
    data?: any,
    config?: PuFetchRequestConfig,
  ): Promise<T>
}

export interface PuFetchInstance {
  request: PuFetchRequest

  get<T>(url: string, config?: PuFetchRequestConfig): Promise<T>
  delete<T>(url: string, config?: PuFetchRequestConfig): Promise<T>
  post<T>(url: string, data?: any, config?: PuFetchRequestConfig): Promise<T>
  put<T>(url: string, data?: any, config?: PuFetchRequestConfig): Promise<T>
  patch<T>(url: string, data?: any, config?: PuFetchRequestConfig): Promise<T>
}

@injectable()
export abstract class PuFetchAbstract {
  @inject(puFetchInjectionKey)
  protected request: PuFetchRequest

  abstract get<T>(url: string, config?: PuFetchRequestConfig): Promise<T>
  abstract delete<T>(url: string, config?: PuFetchRequestConfig): Promise<T>
  abstract post<T>(
    url: string,
    data?: any,
    config?: PuFetchRequestConfig,
  ): Promise<T>
  abstract put<T>(
    url: string,
    data?: any,
    config?: PuFetchRequestConfig,
  ): Promise<T>
  abstract patch<T>(
    url: string,
    data?: any,
    config?: PuFetchRequestConfig,
  ): Promise<T>
}

@injectable()
export default class PuFetch extends PuFetchAbstract {
  get<T = any>(url: string, config?: PuFetchRequestConfig): Promise<T> {
    return this.request.$get<T>(url, config)
  }

  delete<T = any>(url: string, config?: PuFetchRequestConfig): Promise<T> {
    return this.request.$delete(url, config)
  }

  patch<T = any>(
    url: string,
    data?: any,
    config?: PuFetchRequestConfig,
  ): Promise<T> {
    return this.request.$patch(url, data, config)
  }

  post<T = any>(
    url: string,
    data?: any,
    config?: PuFetchRequestConfig,
  ): Promise<T> {
    return this.request.$post(url, data, config)
  }

  put<T = any>(
    url: string,
    data?: any,
    config?: PuFetchRequestConfig,
  ): Promise<T> {
    return this.request.$put(url, data, config)
  }
}
